import { z } from "zod";
import { symbolSchema } from "./instrument";

/**
 * THE AGENT ACTION CONTRACT.
 *
 * Sibling of `insight-schema.ts`, and the division between them is the whole
 * regulatory posture of `ADR-0006`: that file bounds what an agent may **say**,
 * this one bounds what it may **do**. An agent that says "you should sell NVDA"
 * is making a recommendation; an agent that emits a sell intent is operating a
 * simulator. The two travel through different schemas on purpose, and neither
 * is allowed to carry the other's payload.
 *
 * This schema is not a safety mechanism and must not be mistaken for one. It
 * says what shape a decision has, not what a decision is allowed to amount to —
 * the PolicyGate inside `AccountDO.#submitSerialized` does that, below every
 * entry point, so a prompt failure is contained by the venue rather than by the
 * prompt. What this file guarantees is narrower and still load-bearing: **an
 * intent this parser accepts is an intent a human could have typed.** Anything
 * else fails the run.
 *
 * Strictness is the point. `parse` refuses an unknown key rather than ignoring
 * it, because a model that invented `leverage` or `target_weight` has not
 * produced a decision with a harmless extra field — it has produced a decision
 * about something this system does not model, and acting on the parts we
 * recognise would be acting on a misunderstanding.
 */

/**
 * `hold` is an action, not the absence of one.
 *
 * A model that has looked at a symbol and decided to do nothing is saying
 * something different from a model that did not look, and the difference is
 * exactly what `M5/I6`'s reflection memory and `M6`'s scoring need to read back.
 * So a hold is an intent with a rationale, and its effect is a recorded no-op.
 */
export const DECISION_ACTIONS = ["buy", "sell", "hold"] as const;
export type DecisionAction = (typeof DECISION_ACTIONS)[number];

/**
 * Two order types, not the OMS's five.
 *
 * `stop`, `stop_limit` and `trailing_stop` all rest in a `SymbolBookDO` waiting
 * on a trigger that may fire hours after the run that placed it — so the model
 * that chose the trigger is not the model that will be measured on the fill, and
 * an agent's score would include outcomes it never decided. They are deliberately
 * withheld until `M5/I6` gives an agent a way to reason about its own resting
 * orders; a narrower vocabulary that is scoreable beats a complete one that is
 * not.
 */
export const DECISION_ORDER_TYPES = ["market", "limit"] as const;
export type DecisionOrderType = (typeof DECISION_ORDER_TYPES)[number];

/**
 * Quantities and prices are decimal STRINGS, never JSON numbers.
 *
 * The same rule `order.ts` states for the API edge, and here it is doing more
 * work rather than less. At the edge a JSON number can be refused; from a model
 * it is worse than that — `JSON.parse` has already produced the IEEE double
 * before any validation gets a turn, and the model's own tokenizer emitted the
 * digits. A string keeps the digits the model actually chose, all the way to the
 * OMS, which is the only version of "the agent ordered 0.1 shares" that is true.
 */
const decimalString = z
  .string()
  .regex(/^\d+(\.\d+)?$/, "must be a positive decimal string, not a JSON number");

const positiveDecimalString = decimalString.refine(
  (s) => /[1-9]/.test(s),
  "must be greater than zero",
);

/**
 * How much of the answer a model is allowed to spend on explaining itself.
 *
 * Bounded because it is stored, replayed into later runs as memory, and shown to
 * a person — and because an unbounded free-text field on a schema-constrained
 * call is where a truncated answer comes from. 600 characters is a paragraph.
 */
const RATIONALE_MAX = 600;

/**
 * The cap on how many intents one run may produce.
 *
 * A hard number rather than a policy knob, and it belongs *here* rather than in
 * the PolicyGate for a reason the gate cannot serve: the gate refuses intents one
 * at a time, so a model emitting two hundred of them would produce two hundred
 * audited refusals and the effect loop would walk all of them. Bounding the list
 * at the parser bounds the *work*, which is the same argument the runtime makes
 * for one model call per run.
 *
 * Eight is a portfolio decision, not a strategy rewrite. A run that wants more
 * is a run that should have been two runs.
 */
export const MAX_INTENTS_PER_DECISION = 8;

export const decisionIntentSchema = z
  .object({
    action: z.enum(DECISION_ACTIONS),
    symbol: symbolSchema,
    /**
     * Required even for `hold`, where it must be `"0"`.
     *
     * An optional field is a field a model may omit for the wrong reason, and a
     * `hold` that omitted `qty` is indistinguishable from a `buy` whose quantity
     * the model forgot. Requiring the zero makes the no-op explicit and makes the
     * cross-field rule below total.
     */
    qty: decimalString,
    type: z.enum(DECISION_ORDER_TYPES),
    /** Required when `type` is `limit`, refused otherwise. See the refinement. */
    limit_price: positiveDecimalString.optional(),
    rationale: z.string().min(1).max(RATIONALE_MAX),
    /**
     * How sure the model claims to be, 0..1.
     *
     * A JSON number on purpose, and the one place in this system where that is
     * correct: it is not money, nothing is computed from it, and it is stored so
     * `M6` can ask whether a strategy's confidence predicts anything at all. A
     * decimal string here would be pedantry wearing the money rule's clothes.
     */
    confidence: z.number().min(0).max(1),
  })
  .strict()
  .superRefine((intent, ctx) => {
    // A limit order without a price is not an order, and a market order with one
    // is a model that believes it constrained a price it did not. Both are
    // refusals rather than corrections: guessing which half the model meant is
    // exactly the "never a guessed trade" line.
    if (intent.type === "limit" && intent.limit_price === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["limit_price"],
        message: "a limit order must carry a limit_price",
      });
    }
    if (intent.type === "market" && intent.limit_price !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["limit_price"],
        message: "a market order must not carry a limit_price",
      });
    }
    if (intent.action === "hold") {
      if (/[1-9]/.test(intent.qty)) {
        ctx.addIssue({
          code: "custom",
          path: ["qty"],
          message: 'a hold does nothing, so its qty must be "0"',
        });
      }
      if (intent.type !== "market") {
        ctx.addIssue({
          code: "custom",
          path: ["type"],
          message: 'a hold places no order, so its type must be "market"',
        });
      }
    } else if (!/[1-9]/.test(intent.qty)) {
      ctx.addIssue({
        code: "custom",
        path: ["qty"],
        message: "a buy or sell must move a quantity greater than zero",
      });
    }
  });

export type DecisionIntent = z.infer<typeof decisionIntentSchema>;

export const decisionSchema = z
  .object({
    intents: z.array(decisionIntentSchema).max(MAX_INTENTS_PER_DECISION),
  })
  .strict();

export type Decision = z.infer<typeof decisionSchema>;

/** Bump when the shape changes; a run pins the pack version that carried it. */
export const DECISION_SCHEMA_VERSION = "1.1.0";

/**
 * The JSON Schema handed to the model, as `LlmSchema.schema`.
 *
 * **`io: "input"` is required and it is also the correct side of the pipe.**
 * `symbolSchema` ends in a `.transform` that uppercases, and zod refuses to
 * render a transform in output mode — `Transforms cannot be represented in JSON
 * Schema`. That error is the useful kind: a JSON Schema constraining a *model*
 * describes what the model must emit, which is `parse`'s input, and output mode
 * would have described what this file hands to the OMS. `insightJsonSchema`
 * gets away with the default only because nothing in it transforms.
 *
 * **Refinements do not survive automatically.** Zod drops `.refine` and
 * `.superRefine` silently, so the cross-field rules are attached to the
 * rendered intent below. The JSON Schema is still only a constraint on
 * generation — best-effort on Workers AI — while `parse` remains the gate that
 * actually runs. The duplication is deliberate: one reduces malformed paid
 * answers and the other makes sure no malformed answer can become a trade.
 */
const renderedDecisionSchema = z.toJSONSchema(decisionSchema, { io: "input" });
const renderedIntent = (
  renderedDecisionSchema as unknown as {
    properties: { intents: { items: Record<string, unknown> } };
  }
).properties.intents.items;
const renderedProperties = renderedIntent.properties as Record<string, unknown>;
const { limit_price: renderedLimitPrice, ...renderedMarketProperties } = renderedProperties;
const requiredIntentFields = ["action", "symbol", "qty", "type", "rationale", "confidence"];
const positiveQtySchema = {
  type: "string",
  pattern: String.raw`^(?:[1-9]\d*(?:\.\d+)?|0+\.\d*[1-9]\d*)$`,
};

renderedIntent.oneOf = [
  {
    type: "object",
    properties: {
      ...renderedMarketProperties,
      action: { const: "hold" },
      qty: { const: "0" },
      type: { const: "market" },
    },
    required: requiredIntentFields,
    additionalProperties: false,
  },
  {
    type: "object",
    properties: {
      ...renderedMarketProperties,
      action: { enum: ["buy", "sell"] },
      qty: positiveQtySchema,
      type: { const: "market" },
    },
    required: requiredIntentFields,
    additionalProperties: false,
  },
  {
    type: "object",
    properties: {
      ...renderedProperties,
      action: { enum: ["buy", "sell"] },
      qty: positiveQtySchema,
      type: { const: "limit" },
      limit_price: renderedLimitPrice,
    },
    required: [...requiredIntentFields, "limit_price"],
    additionalProperties: false,
  },
];

delete renderedIntent.properties;
delete renderedIntent.required;
delete renderedIntent.additionalProperties;

export const decisionJsonSchema = renderedDecisionSchema;

/**
 * The `LlmSchema` a domain pack passes to `LlmPort.complete`.
 *
 * Named here rather than assembled at the call site so the name a provider sees
 * — and logs, and keys a cache by — is one string in one place.
 */
export const decisionLlmSchema = {
  name: "trading_decision",
  schema: decisionJsonSchema,
} as const;

/**
 * Validate a model's answer, strictly, and throw on anything else.
 *
 * Throwing rather than returning a result, because this is what
 * `AgentDomain.parse` is contracted to do: the runtime turns the throw into a
 * `blocked` run with the message attached, and a `blocked` run is a legible
 * outcome a person can read. A lenient parse producing a partial decision is the
 * one behaviour that has no honest reading downstream.
 */
export function parseDecision(raw: unknown): Decision {
  return decisionSchema.parse(raw);
}
